// src/utils/sessionMetadataHelper.ts - Handles session metadata for S3 uploads
import { SessionMetadata, SessionImage, SessionMetadataFile, ScanMetadata, FolderStructure } from '../types/upload.types';
import { Scene, ImageFile } from '../types/core.types';
import { readExifData, ExifData } from './exifReader';
import { DEFAULT_FOCAL_LENGTH_35MM } from '../constants/upload.constants';
import { loadImageDimensions as loadImageDimensionsAdvanced } from './imageValidation';
import { getFileType } from './fileTypeDetection';


// Enhanced logging utility
const logger = {
  info: (message: string, data?: any) => {
    console.log(`[SessionMetadataHelper] ${message}`, data || '');
  },
  warn: (message: string, data?: any) => {
    console.warn(`[SessionMetadataHelper] ${message}`, data || '');
  },
  error: (message: string, error?: any) => {
    console.error(`[SessionMetadataHelper] ${message}`, error || '');
  }
};

export interface BlurStatistics {
  blurryImages: number;
  blurryPercentage: number;
  totalImages: number;
}

export interface ImageMetadataResult {
  sessionImage: SessionImage;
  error?: string;
}

export interface ProgressCallback {
  (step: string, progress: number, currentItem?: string): void;
}

export class SessionMetadataHelper {
  private sessionUUID: string;
  private folderStructure: FolderStructure;
  private sessionImages: SessionImage[] = [];
  private scene: Scene;
  private baseTimestamp: number;
  private blurStatistics: BlurStatistics;
  private blurryFileNames: string[] = [];
  private progressCallback?: ProgressCallback;
  private hasExifData: boolean = true; // Track if dataset has EXIF data
  private exifCache: Map<string, ExifData> = new Map();
  private exifPresenceDetermined: boolean = false;

  private imageCameraIds: Map<string, string> = new Map();

  constructor(sessionUUID: string, folderStructure: FolderStructure, scene: Scene, progressCallback?: ProgressCallback) {
    this.sessionUUID = sessionUUID;
    this.folderStructure = folderStructure;
    this.scene = scene;
    // Ensure scene.type is set for downstream validation
    if (!this.scene.type) {
      const presetMap: Record<string, string> = {
        'drive': 'drive',
        'indoor': 'indoor',
        'object': 'object',
        'outdoor_walk': 'outdoor_walk',
        'drone': 'drone',
        'mix_drone': 'mix_drone',
        'quick': 'quick'
      };
      (this.scene as any).type = presetMap[this.scene.capturePreset as string] || 'unknown';
    }
    this.baseTimestamp = folderStructure.baseTimestamp;
    this.progressCallback = progressCallback;
    this.blurStatistics = {
      blurryImages: 0,
      blurryPercentage: 0,
      totalImages: 0
    };
    
    logger.info('SessionMetadataHelper initialized', {
      sessionUUID,
      sceneId: scene.id,
      sceneName: scene.name,
      folderPath: folderStructure.fullPath
    });
  }

  /**
   * Unified EXIF reader with caching and dataset-level presence detection.
   * This ensures each file is parsed at most once and the first call sets
   * `hasExifData`, emitting a progress update identical to the previous
   * detectExifPresence implementation.
   */
  private async getExifData(file: File): Promise<ExifData> {
    const key = file.name;

    // Return cached result if available
    if (this.exifCache.has(key)) {
      return this.exifCache.get(key)!;
    }

    // Upstream validators may attach parsed EXIF on the File wrapper
    let data: ExifData;
    const anyFile = file as any;
    if (anyFile.exif) {
      data = anyFile.exif as ExifData;
    } else {
      data = await readExifData(file);
      // Stash so any later consumers (even outside this helper) can reuse
      anyFile.exif = data;
    }

    this.exifCache.set(key, data);

    // Decide dataset-level EXIF presence once
    if (!this.exifPresenceDetermined) {
      this.hasExifData = !!(
        data.focalLength ||
        data.focalLength35mm ||
        data.make ||
        data.model ||
        data.width
      );
      this.exifPresenceDetermined = true;
      this.progressCallback?.('Checking EXIF data presence...', 5);
    }

    return data;
  }

  /**
   * Get image dimensions from various sources with fallbacks
   */
/**
 * Get image dimensions using file type-specific strategies
 */
private async getImageDimensions(image: ImageFile): Promise<{ width: number; height: number }> {
  try {
    // Step 1: Check file type
    const fileType = getFileType(image.file);
    logger.info(`Processing ${fileType.toUpperCase()} file: ${image.file.name}`);

    // Step 2: For all formats except EXR, try EXIF first (has orientation-corrected dimensions)
    if (fileType !== 'exr') {
      try {
        const exifData = await this.getExifData(image.file);
        if (exifData.width && exifData.height) {
          logger.info(`Got ${fileType.toUpperCase()} dimensions from EXIF: ${exifData.width}x${exifData.height}`, image.file.name);
          return { width: exifData.width, height: exifData.height };
        }
      } catch (exifError) {
        logger.warn(`${fileType.toUpperCase()} EXIF dimensions failed, falling back`, { fileName: image.file.name });
      }
    }

    // Step 3: For EXR OR any fallback, use the smart loader
    const dimensions = await this.loadImageDimensions(image.file);
    logger.info(`Got dimensions from image loader: ${dimensions.width}x${dimensions.height}`, image.file.name);
    return { width: dimensions.width, height: dimensions.height };
    
  } catch (error) {
    logger.error('Failed to get image dimensions, using fallback', { 
      fileName: image.file.name, 
      error 
    });
    return { width: 1920, height: 1080 };
  }
}

private async loadImageDimensions(file: File): Promise<{ width: number; height: number }> {
  const dimensions = await loadImageDimensionsAdvanced(file);
  return {
    width: dimensions.width,
    height: dimensions.height
  };
}

  /**
   * Get focal length from EXIF data with fallbacks matching iOS implementation
   * Returns null if no EXIF data is present in the dataset
   */
  private async getFocalLength(image: ImageFile): Promise<number | null> {
  // Check file type before attempting EXIF extraction
  const fileType = getFileType(image.file);
  if (fileType === 'exr') {
    logger.info(`🚫 Skipping focal length extraction - ${fileType.toUpperCase()} format has no EXIF data`);
    return null;
  }

  // If no EXIF data in dataset, skip focal length extraction entirely
  if (!this.hasExifData) {
    logger.info(`🚫 Skipping focal length extraction - no EXIF data in dataset`);
    return null;
  }

  try {
    logger.info(`Extracting focal length for ${image.file.name}`);
    const exifData = await this.getExifData(image.file);
    
    logger.info(`EXIF data extracted:`, {
      fileName: image.file.name,
      focalLength35mm: exifData.focalLength35mm,
      focalLength: exifData.focalLength,
      make: exifData.make,
      model: exifData.model
    });
    
    // Priority 1: Use 35mm equivalent focal length from EXIF - this is the most accurate
    if (exifData.focalLength35mm && exifData.focalLength35mm > 0) {
      logger.info(`✅ Using EXIF 35mm equivalent focal length: ${exifData.focalLength35mm}mm`, image.file.name);
      return exifData.focalLength35mm;
    }
    
    // Priority 2: Use actual focal length from EXIF if available
    if (exifData.focalLength && exifData.focalLength > 0) {
      logger.info(`✅ Using EXIF actual focal length: ${exifData.focalLength}mm`, image.file.name);
      return exifData.focalLength;
    }
    
    // Priority 3: Try to determine device type from EXIF make/model ONLY if no focal length data
    if (exifData.make || exifData.model) {
      const deviceType = this.determineDeviceType(exifData.make, exifData.model);
      if (deviceType) {
        const deviceFocalLength = this.getDeviceTypeFocalLength(deviceType);
        logger.warn(`⚠️ No EXIF focal length found, using device-specific fallback: ${deviceFocalLength}mm`, {
          fileName: image.file.name,
          make: exifData.make,
          model: exifData.model,
          deviceType
        });
        return deviceFocalLength;
      }
    }
    
    // No focal length data found - log and return null
    logger.warn(`⚠️ No focal length data available for ${image.file.name} - focal length will be omitted from metadata`);
    return null;
    
  } catch (error) {
    logger.error('Failed to read focal length from EXIF', { fileName: image.file.name, error });
    logger.warn(`⚠️ EXIF reading failed for ${image.file.name} - focal length will be omitted from metadata`);
    return null;
  }
}

  /**
   * Determine device type from EXIF make/model (like iOS does)
   */
  private determineDeviceType(make: string | null, model: string | null): 'ultrawide' | 'wide' | null {
    if (!make || !model) return null;
    
    const makeLower = make.toLowerCase();
    const modelLower = model.toLowerCase();
    
    // iPhone detection
    if (makeLower.includes('apple') || modelLower.includes('iphone')) {
      // Try to detect if this is an ultrawide camera based on focal length patterns
      // This is a simplified version - iOS uses more sophisticated detection
      if (modelLower.includes('ultra') || modelLower.includes('0.5x')) {
        return 'ultrawide';
      }
      return 'wide';
    }
    
    // Android phones often have multiple cameras
    if (makeLower.includes('samsung') || makeLower.includes('google') || 
        makeLower.includes('huawei') || makeLower.includes('xiaomi')) {
      // Could potentially detect ultrawide vs main camera here
      // For now, default to wide
      return 'wide';
    }
    
    return null;
  }

  /**
   * Get device type focal length (matching iOS defaults)
   */
  private getDeviceTypeFocalLength(deviceType: 'ultrawide' | 'wide'): number {
    switch (deviceType) {
      case 'ultrawide':
        return 13.0; // iOS default for ultrawide camera
      case 'wide':
        return 26.0; // iOS default for wide/main camera
      default:
        return 26.0;
    }
  }

  /**
   * Calculate blur statistics from actual blur detection (iOS-style)
   */
  private async calculateBlurStatistics(images: ImageFile[]): Promise<BlurStatistics> {
    try {
      // Import here so we don't pay the cost if blur detection never runs
      const { BlurDetector } = await import('./blurDetection');
      const blurDetector = new BlurDetector();

      const total = images.length;
      let blurryCount = 0;
      const blurryFlags: boolean[] = [];

      logger.info('Starting incremental blur detection', { totalImages: total });

      this.progressCallback?.('Starting blur detection analysis...', 0);

      for (let i = 0; i < total; i++) {
        const img = images[i];

        try {
          const result = await blurDetector.detectBlur(img.file);
          blurryFlags.push(result.isBlurry);
          if (result.isBlurry) blurryCount++;
        } catch (err) {
          logger.error('Blur detection error for image', { idx: i, file: img.file.name, err });
          blurryFlags.push(false); // Mark as not blurry on error
        }

        // Emit progress as 0-100 based on images processed
        const percent = Math.round(((i + 1) / total) * 100);
        this.progressCallback?.(`Analyzing images for blur… (${i + 1}/${total})`, percent);
      }

      this.progressCallback?.('Blur detection completed', 100);

      const stats = {
        blurryImages: blurryCount,
        blurryPercentage: Math.round((blurryCount / total) * 100),
        totalImages: total
      };

      // Save list
      this.blurryFileNames = images
        .filter((img, idx) => idx < blurryFlags.length ? blurryFlags[idx] : false)
        .map(img => img.file.name);

      logger.info('Final blur statistics', { stats });
      return stats;

    } catch (error) {
      logger.error('Blur detection failed', { error, imageCount: images.length });
      this.progressCallback?.('Blur detection failed, using fallback', 100);
      // Fallback: use quality-based detection
      const qualityBlurStats = this.calculateBlurStatisticsFromQuality(images);
      logger.warn('Using quality-based blur detection fallback', { qualityBlurStats });
      return qualityBlurStats;
    }
  }

  /**
   * Update blur statistics based on processed images (now async to support iOS-style detection)
   */
  async updateBlurStatistics(images: ImageFile[]): Promise<void> {
    try {
      this.blurStatistics = await this.calculateBlurStatistics(images);
      logger.info('Updated blur statistics', this.blurStatistics);
    } catch (error) {
      logger.error('Failed to calculate blur statistics', error);
      // Use safe defaults
      this.blurStatistics = {
        blurryImages: 0,
        blurryPercentage: 0,
        totalImages: images.length
      };
    }
  }

  /**
   * Add an image to the session metadata with proper error handling and progress tracking
   */
  async addImage(
    image: ImageFile, 
    sequenceNumber: number,
    orderedFilename: string,
    providedDimensions: { width: number; height: number } | null = null,
    cameraId?: string
  ): Promise<ImageMetadataResult> {
    try {
      logger.info(`Processing image ${sequenceNumber + 1}`, {
        fileName: image.file.name,
        orderedFilename,
        sequenceNumber,
        cameraId: cameraId || image.cameraId || 'camera1'
      });

      this.progressCallback?.(`Processing metadata for ${image.file.name}`, 0, image.file.name);

      // Calculate timestamp: base timestamp + sequence number in seconds (converted to milliseconds)
      const imageTimestamp = this.baseTimestamp + (sequenceNumber * 1000);
      
      this.progressCallback?.(`Reading image dimensions...`, 25, image.file.name);
      
      // Get image dimensions with proper fallbacks
      const dimensions = providedDimensions || await this.getImageDimensions(image);
      
      // Create image key with no manual escaping to match iOS JSONSerialization behavior
      const imageKey = `${this.folderStructure.fullPath}/${orderedFilename}`;

      // Create base session image without focal length
      const sessionImage: Partial<SessionImage> = {
        image_height_px: dimensions.height,
        image_key: imageKey,
        timestamp: imageTimestamp.toString(),
        image_width_px: dimensions.width
      };

      // Only add focal length if EXIF data is present in the dataset
      if (this.hasExifData) {
        this.progressCallback?.(`Extracting focal length from EXIF...`, 75, image.file.name);
        const focalLength = await this.getFocalLength(image);
        
        if (focalLength !== null) {
          (sessionImage as SessionImage).focal_length_35mm = focalLength;
        }
      } else {
        logger.info(`🚫 Skipping focal length extraction - no EXIF data in dataset`);
        this.progressCallback?.(`Skipping focal length extraction...`, 75, image.file.name);
      }
      
      this.progressCallback?.(`Finalizing metadata...`, 90, image.file.name);
      //save camera id with image
      const finalCameraId = cameraId || image.cameraId || 'camera1';
      this.imageCameraIds.set(imageKey, finalCameraId);

      this.sessionImages.push(sessionImage as SessionImage);
      
      this.progressCallback?.(`Completed processing ${image.file.name}`, 100, image.file.name);
      
      logger.info(`Successfully added image to session metadata`, {
        filename: orderedFilename,
        timestamp: imageTimestamp,
        dimensions: `${dimensions.width}x${dimensions.height}`,
        focalLength: this.hasExifData ? `${(sessionImage as SessionImage).focal_length_35mm || 'N/A'}mm` : 'Skipped (no EXIF)',
        key: sessionImage.image_key,
        cameraId: finalCameraId
      });

      return { sessionImage: sessionImage as SessionImage };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error(`Failed to process image metadata`, {
        fileName: image.file.name,
        sequenceNumber,
        error: errorMessage
      });

      this.progressCallback?.(`Error processing ${image.file.name}: ${errorMessage}`, 100, image.file.name);

      // Create fallback session image
      const fallbackImage: Partial<SessionImage> = {
        image_height_px: providedDimensions?.height || 1080,
        image_key: `${this.folderStructure.fullPath}/${orderedFilename}`,
        timestamp: (this.baseTimestamp + (sequenceNumber * 1000)).toString(),
        image_width_px: providedDimensions?.width || 1920
      };

      // Only add focal length to fallback if EXIF data should be present
      if (this.hasExifData) {
        (fallbackImage as SessionImage).focal_length_35mm = DEFAULT_FOCAL_LENGTH_35MM;
      }

      return {
        sessionImage: fallbackImage as SessionImage,
        error: errorMessage
      };
    }
  }

  /**
   * Generate the SessionMetadata object
   */
  private generateSessionMetadata(): SessionMetadata {
    // Group images by camera ID
    const imagesByCamera = new Map<string, SessionImage[]>();
    
    this.sessionImages.forEach(image => {
      const cameraId = this.imageCameraIds.get(image.image_key) || 'camera1';
      if (!imagesByCamera.has(cameraId)) {
        imagesByCamera.set(cameraId, []);
      }
      imagesByCamera.get(cameraId)!.push(image);
    });

    // Sort cameras by ID to ensure consistent ordering (camera1, camera2, camera3, camera4)
    const sortedCameraIds = Array.from(imagesByCamera.keys()).sort();

    // Create sequences array - one sequence per camera
    const sequences = sortedCameraIds.map(cameraId => {
      const cameraImages = imagesByCamera.get(cameraId)!;
      logger.info(`Camera ${cameraId}: ${cameraImages.length} images`);
      return cameraImages;
    });

    const metadata: any = {
      sequence_set_uuid: this.sessionUUID,  // New field
      preset: this.scene.capturePreset,
      options: {},  // New field
      sequences: sequences  // Grouped by camera
    };
    
    logger.info('Generated session metadata with multi-camera support', {
      sequence_set_uuid: this.sessionUUID,
      totalSequences: sequences.length,
      totalImages: this.sessionImages.length,
      cameraCounts: Object.fromEntries(
        sortedCameraIds.map(cameraId => [cameraId, imagesByCamera.get(cameraId)!.length])
      )
    });
    
    return metadata as SessionMetadata;
  }
  /**
   * Generate the complete SessionMetadataFile object with enhanced error handling
   */
  generateSessionMetadataFile(): SessionMetadataFile {
    try {
      const sessionMetadata = this.generateSessionMetadata();
      
      // Spread metadata fields directly 
      const metadataFile = {
        sessionUUID: this.sessionUUID,
        destinationFolder: this.folderStructure.fullPath,
        blurStatistics: { ...this.blurStatistics },
        ...sessionMetadata  // Spread all metadata fields directly
      };
  
      logger.info('Generated session metadata file', {
        sessionUUID: this.sessionUUID,
        destinationFolder: this.folderStructure.fullPath,
        sequenceSetUuid: sessionMetadata.sequence_set_uuid,
        sequenceCount: sessionMetadata.sequences.length,
        blurStatistics: this.blurStatistics
      });
  
      return metadataFile as SessionMetadataFile;
    } catch (error) {
      logger.error('Failed to generate session metadata file', error);
      throw new Error(`Failed to generate session metadata: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate the ScanMetadata object based on the scene with validation
   */
  generateScanMetadata(): ScanMetadata {
    try {
      // Validate scene data
      if (!this.scene.name || this.scene.name.trim() === '') {
        throw new Error('Scene name is required');
      }

    const cameraTypeMap: Record<string, string> = {
      'drone': 'Drone',
      'professional': 'Professional',
      'insta360': 'Insta360',
      'smartphone': 'Smartphone'
    };
 
      // Derive sceneType from capturePreset when available
      const presetMap: Record<string, string> = {
        'drive': 'drive',
        'indoor': 'indoor',
        'object': 'object',
        'outdoor_walk': 'outdoor_walk',
        'drone': 'drone',
        'mix_drone': 'mix_drone',
        'quick': 'quick'
      };

      const derivedSceneType = presetMap[(this.scene.capturePreset as string) || ''] || 'unknown';

      const scanMetadata: any = {
        // Convert to seconds like iOS (seconds since epoch)
        timestamp: Math.floor(this.baseTimestamp / 1000),
        scanId: this.sessionUUID,
        capturePreset: this.scene.capturePreset,
        sceneName: this.scene.name.trim(),
        description: this.scene.description?.trim() || '',
        tempFolderPath: this.folderStructure.fullPath,
        sceneType: derivedSceneType,
        cameraType: cameraTypeMap[this.scene.cameraType] || this.scene.cameraType,
      };

      if (this.scene.location) {
        scanMetadata.location = this.scene.location;
      }

      // Add blurry image list
      if (this.blurryFileNames.length) {
        (scanMetadata as any).blurryImages = this.blurryFileNames;
      }

      logger.info('Generated scan metadata', {
        scanId: scanMetadata.scanId,
        sceneName: scanMetadata.sceneName,
        sceneType: scanMetadata.sceneType,
        cameraType: scanMetadata.cameraType,
        captureSetup: scanMetadata.captureSetup
      });

      return scanMetadata as unknown as ScanMetadata;
    } catch (error) {
      logger.error('Failed to generate scan metadata', error);
      throw new Error(`Failed to generate scan metadata: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate JSON string for sessionMetadata.json file with error handling
   */
  generateSessionMetadataJSON(): string {
    try {
    const sessionMetadataFile = this.generateSessionMetadataFile();
      const json = JSON.stringify(sessionMetadataFile, null, 2);
      logger.info('Generated session metadata JSON', { size: json.length });
      return json;
    } catch (error) {
      logger.error('Failed to generate session metadata JSON', error);
      throw error; // Re-throw to maintain error handling chain
    }
  }

  /**
   * Generate JSON string for scanMetadata.json file with error handling
   */
  generateScanMetadataJSON(): string {
    try {
    const scanMetadata = this.generateScanMetadata();
      const json = JSON.stringify(scanMetadata, null, 2);
      logger.info('Generated scan metadata JSON', { size: json.length });
      return json;
    } catch (error) {
      logger.error('Failed to generate scan metadata JSON', error);
      throw error; // Re-throw to maintain error handling chain
    }
  }

  /**
   * Convert timestamp to IST and format for use in filenames
   */
  private static toISTTimestamp(timestamp: number): string {
    // IST is UTC+5:30 (5.5 hours = 19800000 milliseconds)
    const istTimestamp = timestamp + (5.5 * 60 * 60 * 1000);
    const date = new Date(istTimestamp);
    
    // Format timestamp as YYYY-MM-DD_HH-mm-ss in IST
    const year = date.getUTCFullYear(); // Use UTC methods since we already converted to IST
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  }

  /**
   * Generate ordered filename based on timestamp (using IST)
   */
  static generateOrderedFilename(
    originalFilename: string, 
    baseTimestamp: number, 
    sequenceNumber: number
  ): string {
    try {
    // Calculate the timestamp for this image
    const imageTimestamp = baseTimestamp + (sequenceNumber * 1000);
    
    // Format timestamp using IST
    const formattedTimestamp = SessionMetadataHelper.toISTTimestamp(imageTimestamp);
    
      // Extract and normalise file extension
      const lastDotIndex = originalFilename.lastIndexOf('.');
      let extension = '.jpg'; // default/common
      if (lastDotIndex > -1) {
        const rawExt = originalFilename.slice(lastDotIndex).toLowerCase();
        // normalise .jpeg -> .jpg, otherwise keep lower-case ext
        extension = rawExt === '.jpeg' ? '.jpg' : rawExt;
      }
    
    // Generate filename: photo_YYYY-MM-DD_HH-mm-ss.ext
      const filename = `photo_${formattedTimestamp}${extension}`;
      
      logger.info('Generated ordered filename with IST timestamp', {
        original: originalFilename,
        generated: filename,
        sequenceNumber,
        istTimestamp: formattedTimestamp
      });
      
      return filename;
    } catch (error) {
      logger.error('Failed to generate ordered filename', { originalFilename, sequenceNumber, error });
      
      // Fallback to a safe filename - try to preserve original extension
      const originalExt = originalFilename.includes('.') ? 
        originalFilename.slice(originalFilename.lastIndexOf('.')) : '.jpg';
      return `photo_${Date.now()}_${sequenceNumber}${originalExt}`;
    }
  }

  /**
   * Get the current session UUID
   */
  getSessionUUID(): string {
    return this.sessionUUID;
  }

  /**
   * Get the number of images added
   */
  getImageCount(): number {
    return this.sessionImages.length;
  }

  /**
   * Get current blur statistics
   */
  getBlurStatistics(): BlurStatistics {
    return { ...this.blurStatistics };
  }

  /**
   * Set blur statistics & filenames from worker (overlapped mode)
   */
  setBlurResults(stats: BlurStatistics, blurryFiles: string[]): void {
    this.blurStatistics = stats;
    this.blurryFileNames = blurryFiles;
  }

  /**
   * Validate metadata integrity before upload
   */
  validateMetadata(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    try {
      // Validate session data
      if (!this.sessionUUID || this.sessionUUID.trim() === '') {
        errors.push('Session UUID is required');
      }

      if (!this.folderStructure.fullPath || this.folderStructure.fullPath.trim() === '') {
        errors.push('Folder path is required');
      }

      // Validate scene data
      if (!this.scene.name || this.scene.name.trim() === '') {
        errors.push('Scene name is required');
      }

      if (!this.scene.type) {
        errors.push('Scene type is required');
      }

      if (!this.scene.cameraType) {
        errors.push('Camera type is required');
      }

      if (!this.scene.capturePreset) {
        errors.push('Capture preset is required');
      }

      // Validate images
      if (this.sessionImages.length === 0) {
        errors.push('At least one image is required');
      }

      // Validate each session image
      this.sessionImages.forEach((image, index) => {
        if (!image.image_key || image.image_key.trim() === '') {
          errors.push(`Image ${index + 1}: Image key is required`);
        }
        if (!image.timestamp) {
          errors.push(`Image ${index + 1}: Timestamp is required`);
        }
        if (image.image_width_px <= 0 || image.image_height_px <= 0) {
          errors.push(`Image ${index + 1}: Invalid dimensions`);
        }
        // Only validate focal length if EXIF data is present in dataset
        if (this.hasExifData && image.focal_length_35mm !== undefined && image.focal_length_35mm <= 0) {
          errors.push(`Image ${index + 1}: Invalid focal length`);
        }
      });

    } catch (error) {
      errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    const isValid = errors.length === 0;
    
    if (!isValid) {
      logger.error('Metadata validation failed', errors);
    } else {
      logger.info('Metadata validation passed');
    }

    return { isValid, errors };
  }

  /**
   * Fallback method for calculating blur statistics from quality data
   */
  private calculateBlurStatisticsFromQuality(images: ImageFile[]): BlurStatistics {
    const totalImages = images.length;
    let blurryImages = 0;

    for (const image of images) {
      if (image.quality) {
        // Consider an image blurry if sharpness is below 60% or has motion blur issues
        const isBlurry = image.quality.sharpness < 0.6 || 
                        image.quality.motionBlur < 0.7 ||
                        image.quality.technicalDetails?.blurScore > 0.4;
        
        if (isBlurry) {
          blurryImages++;
        }
      }
    }

    const blurryPercentage = totalImages > 0 ? Math.round((blurryImages / totalImages) * 100) : 0;
    
    return {
      blurryImages,
      blurryPercentage,
      totalImages
    };
  }

  async detectExifPresence(images: ImageFile[]): Promise<void> {
    // Lightweight shim retained for backward compatibility
    if (this.exifPresenceDetermined || images.length === 0) return;
    await this.getExifData(images[0].file);
  }
} 